import {
    BufferGeometry,
    CircleGeometry,
    Color,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    RingGeometry,
    ShaderMaterial,
    Vector3
} from "three";
import {MarkerModel} from "features/apartmentViewer/ApartmentViewer/models/MarkerModel";
import {ApartmentModel} from "features/apartmentViewer/ApartmentViewer/models/ApartmentModel";
import {
    MarkerMaterialFactory
} from "features/apartmentViewer/ApartmentViewer/shaders/markerMaterial/MarkerMaterialFactory";
import {mergeBufferGeometries} from "three-stdlib";
import {CameraModel} from "features/apartmentViewer/ApartmentViewer/models/CameraModel";
import {PanoramaManager} from "features/apartmentViewer/ApartmentViewer/services/PanoramaManager";

const INNER_RADIUS = 0.1;
const OUTER_RADIUS = 0.15;
const SEGMENTS_RING = 64;
const SEGMENTS_DISK = 32;
const ROTATION_ANGLE = -Math.PI / 2;


export class MarkerService {
    private readonly data: Map<string, MarkerModel> = new Map();
    private _panoramaManager: PanoramaManager | undefined;


    constructor() {}

    addMarker(marker: MarkerModel) {
        this.data.set(marker.id, marker);
    }

    getMarkerById(id: string): MarkerModel | undefined {
        return this.data.get(id);
    }

    getFirstMarker() {
        if (this.data.size === 0) return;
        const firstMarker = this.data.entries().next().value;
        return firstMarker ? firstMarker[1] : undefined;
    }

    getMarkers() {
        return this.data;
    }

    hasMarkers() {
        return this.data.size > 0
    }


    async addMarkers(apartment: ApartmentModel) {
        await Promise.all(apartment.cameras.map(async (camera) => {
            const ringGeometry = this.createRingGeometry(camera);
            const diskGeometry = this.createDiskGeometry(camera);
            const combinedGeometry = this.createCombinedGeometry(ringGeometry, diskGeometry);

            const [ringMaterial, transparentMaterial] = this.createMaterials();

            const mesh = new Mesh(combinedGeometry, [ringMaterial, transparentMaterial]);

            const marker = new MarkerModel(
                camera.id,
                camera.position,
                new Vector3(0, 0, 0),
                mesh,
                camera
            );

            this.addMarker(marker);
        }));

    }

    private createRingGeometry(camera: CameraModel): RingGeometry {
        const ringGeometry = new RingGeometry(INNER_RADIUS, OUTER_RADIUS, SEGMENTS_RING);
        this.applyTransformations(ringGeometry, camera);
        return ringGeometry;
    }

    private createDiskGeometry(camera: CameraModel): CircleGeometry {
        const diskGeometry = new CircleGeometry(INNER_RADIUS, SEGMENTS_DISK);
        this.applyTransformations(diskGeometry, camera);
        return diskGeometry;
    }

    private applyTransformations(geometry: BufferGeometry, camera: CameraModel): void {
        geometry.applyMatrix4(new Matrix4().makeRotationX(ROTATION_ANGLE).multiply(new Matrix4().makeScale(0.75, 0.75, 0.75)));
        geometry.applyMatrix4(new Matrix4().makeTranslation(0, camera.floorVerticalPosition - camera.position.y, 0));
    }

    private createMaterials(): [ShaderMaterial, MeshBasicMaterial] {
        const ringMaterial = MarkerMaterialFactory.createMaterial(new Color(0, 0, 0), -0.2);
        const transparentMaterial = new MeshBasicMaterial({
            color: new Color(0, 0, 0),
            opacity: 0.0,
            transparent: true,
        });
        return [ringMaterial, transparentMaterial];
    }

    private createCombinedGeometry(ringGeometry: RingGeometry, diskGeometry: CircleGeometry): BufferGeometry {
        const combinedGeometry = mergeBufferGeometries([ringGeometry, diskGeometry], true);
        if (!combinedGeometry) {
            throw new Error("Failed to merge geometries. Ensure geometries are valid.");
        }

        combinedGeometry.clearGroups();
        combinedGeometry.addGroup(0, ringGeometry.index?.count ?? 0, 0); // First material (ring)
        combinedGeometry.addGroup(ringGeometry.index?.count ?? 0, diskGeometry.index?.count ?? 0, 1); // Second material (transparent disk)

        return combinedGeometry;
    }

    get panoramaManager(): PanoramaManager | undefined {
        return this._panoramaManager;
    }

    set panoramaManager(value: PanoramaManager) {
        this._panoramaManager = value;
    }

    update() {

        this.data.forEach((marker) => {
            marker.update();

            marker.model.visible = this.panoramaManager
                ? marker.setIsVisible(this.panoramaManager.getCurrentPanorama())
                : false;
        });
    }
}
